import L from 'leaflet';
import 'leaflet-realtime';
import { toString, assign, forEach, hasIn, isEqual, set, get, isEmpty, map } from 'lodash';
import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';
import methodPromise from '../method-promise-wrapper';
import adaptURL from '../adapt-url';
import { getGeoJSONStyle } from '../geojson-layer-style';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import loadStyleFromURL from '../load-style-from-url';
/**
 * Builds the RealTime layer.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @param {boolean} [arg.isPartOfMap=false] - Flag that is true if layer is part of a map.
 * @param {number} [arg.zIndex=400] - The zIndex for the layer.
 * @returns {Promise<object>} The leaflet layer to add to the map with the url and the options.
 */
const buildRealTimeLayer = async ({ layer, style, isPartOfMap = false, zIndex = 400 }) => {
  let layerToDraw = '';
  let styleOptions;
  const options = {
    interval: layerOps.getInterval(layer),
    getFeatureId: feature =>
      JSON.stringify(
        get(feature, ['properties'], { uuid: get(feature, ['properties', 'uuid'], '0') })
      )
  };
  const url = adaptURL(layerOps.getURL(layer));
  let customStyleObject = {};

  try {
    if (styleOps.isCustom(style))
      customStyleObject = await loadStyleFromURL({
        url: adaptURL(styleOps.getStyleURL(style)),
        layer
      });
    if (isPartOfMap) {
      styleOptions = await getGeoJSONStyle({
        style,
        layer,
        pane: toString(zIndex),
        customStyleObject
      });
      assign(options, {
        ...styleOptions,
        attribution: layerOps.getAttribution(layer),
        pane: toString(zIndex)
      });
    } else {
      styleOptions = await getGeoJSONStyle({ style, layer, customStyleObject });
      assign(options, { ...styleOptions, attribution: layerOps.getAttribution(layer) });
    }
    layerToDraw = L.realtime(async (success, error) => {
      try {
        let geojsonFile;
        if (layerOps.isRealTime(layer)) {
          geojsonFile = await loadGeoJSONFromURL(url);
          forEach(geojsonFile.features, (feature, index) => {
            if (!hasIn(feature, ['properties', 'uuid']))
              set(feature, ['properties', 'uuid'], `${index}`);
          });
        } else if (layerOps.isAnnotationsRealTime(layer)) {
          const oldProperties = layerOps.getAnnotationLayerProperties(layer);
          try {
            const annotationsPromises = oldProperties.videosId.map(async element => {
              const promise = await methodPromise('annotations.annotationsFromVideo', element);
              return promise;
            });
            const newAnnotationsFromVideos = (await Promise.all(annotationsPromises)).flat();
            let geojsonToDraw = [];
            if (oldProperties.geometry.key === 'LineString') {
              geojsonFile = oldProperties.geojsonAnnotations;
            } else if (
              oldProperties.geometry.key === 'Point' &&
              !isEmpty(newAnnotationsFromVideos)
            ) {
              map(newAnnotationsFromVideos, indexElement => {
                if (!isEmpty(indexElement.geoJSON)) {
                  geojsonToDraw = [...geojsonToDraw, indexElement.geoJSON];
                }
              });
              const newAnnotationsId = newAnnotationsFromVideos.map(index => index._id);
              if (!isEqual(newAnnotationsId, oldProperties.annotationsId)) {
                try {
                  await methodPromise(
                    'layers.updateRealTimeAnnotations',
                    layerOps.getId(layer),
                    newAnnotationsId
                  );
                } catch (errorAnnotation) {
                  console.error('Error al actualizar las anotaciones:', errorAnnotation);
                }
              }
              geojsonFile = { type: 'FeatureCollection', features: geojsonToDraw };
            }
          } catch (errVideo) {
            console.error('Error al procesar las anotaciones:', errVideo);
          }
        }
        success(geojsonFile);
      } catch (err) {
        error(err, 'Problem found in the GeoJSON');
      }
    }, options);
    return layerToDraw;
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};
export default buildRealTimeLayer;
